import { useEffect, useState } from "react";
import {
    Loader2,
    RefreshCw,
    CheckCircle,
    CheckCircle2,
    AlertCircle,
    AlertTriangle,
    Rocket,
    Archive,
    FileText,
    ListChecks,
    Image,
    Send,
    Settings,
    Link as LinkIcon,
    HelpCircle,
    Megaphone,
    ShieldCheck,
    Eye,
    Share2,
    Wrench,
} from "lucide-react";
import {
    adminContentPackageQA,
    adminQAAutoFix,
    adminGetContentPackage,
    adminPublishContentPackage,
    adminRegenerateContentSection,
    adminRegenerateHeroImage,
    adminResetContentGeneration,
    adminSendTestEmail,
    adminUpdateContentPackage,
    adminArchiveContentPackage,
} from "@/lib/api";
import { toast } from "sonner";
import DistributionTab from "@/components/admin/content/DistributionTab";
import AssetIndex from "@/components/admin/content/AssetIndex";
import ImagesTab from "@/components/admin/content/ImagesTab";
import QAAutoFixPreviewModal from "@/components/admin/content/QAAutoFixPreviewModal";

// V1 Lean Content Engine — full TAB list. (YouTube + Short-Form Video
// tabs were removed in v4.44.2 when video generation was permanently
// locked — see CHANGELOG.)
const ALL_TABS = [
    { key: "article", label: "Article", icon: FileText },
    { key: "images", label: "Images", icon: Image },
    { key: "mini_tool", label: "Mini Tool", icon: Settings },
    { key: "faq", label: "FAQ", icon: HelpCircle },
    { key: "seo", label: "SEO + Schema", icon: ListChecks },
    { key: "cta_blocks", label: "CTAs", icon: Megaphone },
    { key: "internal_links", label: "Internal Links", icon: LinkIcon },
    { key: "distribution", label: "Distribution", icon: Share2 },
    { key: "preview", label: "Preview", icon: Eye },
    { key: "qa", label: "QA + Publish", icon: ShieldCheck },
];

function SectionHeader({ icon: Icon, title, onRegen, regenLabel = "Regenerate", busy, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-bl/40">
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-bl-primary" />
                <h3 className="font-display font-semibold text-lg text-bl tracking-tight">
                    {title}
                </h3>
            </div>
            {onRegen && (
                <button
                    type="button"
                    onClick={onRegen}
                    disabled={busy}
                    className="bl-btn-ghost text-xs gap-1.5"
                    data-testid={testId || `regen-${title.toLowerCase().replace(/\s+/g, "-")}-btn`}
                >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    {regenLabel}
                </button>
            )}
        </div>
    );
}

export default function ContentPackageEditor({ pkg: initialPkg, onUpdate, onClose }) {
    const [pkg, setPkg] = useState(initialPkg);
    const [tab, setTab] = useState("article");
    const [regenBusy, setRegenBusy] = useState(null);
    const [qaResult, setQaResult] = useState(pkg.qa_status || null);
    const [publishing, setPublishing] = useState(false);
    const [qaBusy, setQaBusy] = useState(false);
    const [qaFixBusy, setQaFixBusy] = useState(false);
    const [qaFixApplying, setQaFixApplying] = useState(false);
    const [qaFixPreview, setQaFixPreview] = useState(null);
    const [qaFixModalOpen, setQaFixModalOpen] = useState(false);
    const [lastFixReport, setLastFixReport] = useState(null);
    const [testEmailBusy, setTestEmailBusy] = useState(false);

    const TABS = ALL_TABS;

    const sendTestEmail = async () => {
        if (testEmailBusy) return;
        setTestEmailBusy(true);
        try {
            const r = await adminSendTestEmail(pkg.package_id);
            toast.success(`Test email sent to ${r.sent_to}`, {
                description: `${r.greeting} · ${r.subject} · ${r.html_size_kb}KB`,
                duration: 5000,
            });
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Send failed";
            toast.error(`Test email failed: ${msg}`);
        } finally {
            setTestEmailBusy(false);
        }
    };

    useEffect(() => {
        setPkg(initialPkg);
        setQaResult(initialPkg.qa_status || null);
    }, [initialPkg]);

    // Auto-poll while EITHER the article-generation background job OR the
    // secondary-asset background job OR the distribution-generation
    // background job is running so the admin sees hero image, social
    // copy, Pinterest pin, and Instagram slide images land as they're
    // generated — no manual refresh needed.
    useEffect(() => {
        const polling =
            pkg?.status === "generating"
            || pkg?.asset_generation_status === "in_progress"
            || pkg?.asset_generation_status === "pending"
            || pkg?.distribution_generation_status === "in_progress"
            || pkg?.hero_regeneration_status === "in_progress";
        if (!polling) return;
        let alive = true;
        const tick = async () => {
            try {
                const fresh = await adminGetContentPackage(pkg.package_id);
                if (!alive || !fresh) return;
                setPkg(fresh);
                onUpdate?.(fresh);
            } catch {
                /* swallow — try again on next tick */
            }
        };
        const id = setInterval(tick, 6000);
        return () => {
            alive = false;
            clearInterval(id);
        };
    }, [
        pkg?.status,
        pkg?.asset_generation_status,
        pkg?.distribution_generation_status,
        pkg?.hero_regeneration_status,
        pkg?.package_id,
        onUpdate,
    ]);

    const regen = async (section) => {
        setRegenBusy(section);
        try {
            const r = await adminRegenerateContentSection(pkg.package_id, section);
            setPkg(r.package);
            onUpdate?.(r.package);
            toast.success(`Regenerated ${section} · ${r.ai_calls} AI call(s).`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || `Failed to regenerate ${section}.`,
            );
        } finally {
            setRegenBusy(null);
        }
    };

    const regenHeroImage = async () => {
        setRegenBusy("hero_image");
        try {
            const r = await adminRegenerateHeroImage(pkg.package_id);
            if (r?.queued) {
                // Background path — mark the package as in-progress so the
                // editor's existing polling watcher picks up the new hero
                // image when the background task lands it in Mongo.
                const next = {
                    ...pkg,
                    hero_regeneration_status: "in_progress",
                    hero_regeneration_started_at: new Date().toISOString(),
                };
                setPkg(next);
                onUpdate?.(next);
                toast.success(
                    "Queued — regenerating hero claymation in background. "
                    + "New image will appear here in ~20s.",
                    { duration: 8000 },
                );
            } else {
                // Backwards-compat: old synchronous path returned hero_image
                // + public_url directly.
                const next = {
                    ...pkg,
                    hero_image: { ...(r.hero_image || {}), url: r.public_url || r.hero_image?.url },
                };
                setPkg(next);
                onUpdate?.(next);
                toast.success(
                    `Hero claymation image regenerated · ${(r.duration_ms / 1000).toFixed(1)}s`,
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Failed to regenerate hero claymation image.",
            );
        } finally {
            setRegenBusy(null);
        }
    };

    const runQA = async () => {
        setQaBusy(true);
        try {
            const r = await adminContentPackageQA(pkg.package_id);
            setQaResult(r);
            toast.success(`QA score ${r.score}/100 · ${r.errors} errors · ${r.warnings} warnings.`);
        } catch {
            toast.error("QA check failed.");
        } finally {
            setQaBusy(false);
        }
    };

    const runAutoFix = async () => {
        if (qaFixBusy) return;
        // Step 1: dry-run preview — compute the diff without persisting.
        setQaFixBusy(true);
        setQaFixPreview(null);
        setQaFixModalOpen(true);
        try {
            const r = await adminQAAutoFix(pkg.package_id, { dryRun: true });
            setQaFixPreview(r);
        } catch (e) {
            setQaFixModalOpen(false);
            toast.error(
                e?.response?.data?.detail || e?.message || "Auto-fix preview failed.",
            );
        } finally {
            setQaFixBusy(false);
        }
    };

    const applyAutoFix = async () => {
        if (qaFixApplying) return;
        setQaFixApplying(true);
        try {
            const r = await adminQAAutoFix(pkg.package_id, { dryRun: false });
            setQaResult(r.qa_status);
            setLastFixReport(r);
            setQaFixModalOpen(false);
            // Refresh the package so the article/SEO/CTA fields reflect the patch.
            try {
                const refreshed = await adminGetContentPackage(pkg.package_id);
                if (refreshed) {
                    setPkg(refreshed);
                    onUpdate?.(refreshed);
                }
            } catch {
                /* swallow — qa_status alone is enough to update the QA tab */
            }
            const fixCount = (r.applied_fixes || []).length;
            const remainingErr = r.qa_status?.errors ?? 0;
            const imgQ = r.images_regen_queued || 0;
            if (fixCount === 0 && imgQ === 0) {
                toast.info("No auto-fixable issues found.");
            } else {
                const parts = [];
                if (fixCount) parts.push(`${fixCount} fix${fixCount === 1 ? "" : "es"} applied`);
                if (imgQ) parts.push(`${imgQ} image${imgQ === 1 ? "" : "s"} re-queued`);
                parts.push(`${remainingErr} error${remainingErr === 1 ? "" : "s"} remain`);
                toast.success(parts.join(" · "));
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Auto-fix failed.",
            );
        } finally {
            setQaFixApplying(false);
        }
    };

    const closeAutoFixModal = () => {
        if (qaFixApplying) return;
        setQaFixModalOpen(false);
        setQaFixPreview(null);
    };

    const publish = async () => {
        if (!window.confirm("Publish this package to the Learn section?")) return;
        setPublishing(true);
        try {
            const r = await adminPublishContentPackage(pkg.package_id);
            toast.success(`Published to /learn/${r.slug}`);
            const updated = { ...pkg, status: "published", published_slug: r.slug };
            setPkg(updated);
            onUpdate?.(updated);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail?.message ||
                    e?.response?.data?.detail ||
                    "Publish failed.",
            );
        } finally {
            setPublishing(false);
        }
    };

    const archive = async () => {
        if (!window.confirm("Archive this package? (won't delete the data)")) return;
        try {
            await adminArchiveContentPackage(pkg.package_id);
            toast.success("Archived.");
            onClose?.();
        } catch {
            toast.error("Archive failed.");
        }
    };

    const updateTitleAndNotes = async (patch) => {
        try {
            const updated = await adminUpdateContentPackage(pkg.package_id, patch);
            setPkg(updated);
            onUpdate?.(updated);
        } catch {
            toast.error("Failed to save.");
        }
    };

    const article = pkg.article_json || {};
    const seo = pkg.seo_metadata || {};
    const faq = pkg.faq || [];
    const miniTool = pkg.mini_tool_config;
    const cta = pkg.cta_blocks || [];
    const internalLinks = pkg.internal_links?.links || [];

    return (
        <div className="space-y-5" data-testid="content-package-editor">
            {/* Header bar */}
            <div className="bl-card p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                        Status: {pkg.status} · {pkg.package_id}
                    </div>
                    <h1
                        className="font-display font-semibold text-2xl text-bl truncate"
                        data-testid="ce-pkg-title"
                    >
                        {pkg.title || article.title || pkg.package_id}
                    </h1>
                    {pkg.published_slug && (
                        <a
                            href={`/learn/${pkg.published_slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-bl-primary underline"
                        >
                            Live → /learn/{pkg.published_slug}
                        </a>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={archive}
                        className="bl-btn-ghost text-xs gap-1"
                        data-testid="ce-archive-btn"
                    >
                        <Archive className="h-3 w-3" /> Archive
                    </button>
                    <button
                        type="button"
                        onClick={sendTestEmail}
                        disabled={testEmailBusy || !pkg.published_slug}
                        className="bl-btn-ghost text-xs gap-1"
                        data-testid="ce-test-email-btn"
                        title={pkg.published_slug ? "Send a preview newsletter render of this article to your admin email" : "Publish the package first"}
                    >
                        {testEmailBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Send className="h-3 w-3" />
                        )}
                        Send test email
                    </button>
                    <button
                        type="button"
                        onClick={publish}
                        disabled={publishing || pkg.status === "published"}
                        className="bl-btn-ghost text-xs gap-1"
                        data-testid="ce-publish-btn"
                    >
                        {publishing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Rocket className="h-3 w-3" />
                        )}
                        {pkg.status === "published" ? "Published" : "Publish only"}
                    </button>
                </div>
            </div>

            {/* Asset index — quick "where everything lives" map */}
            <AssetIndex pkg={pkg} onJumpToTab={setTab} />

            {/* Tab nav */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="ce-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={
                            tab === t.key
                                ? "bl-btn-primary text-xs gap-1.5"
                                : "bl-btn-ghost text-xs gap-1.5"
                        }
                        data-testid={`ce-tab-${t.key}`}
                    >
                        <t.icon className="h-3 w-3" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Stuck-generation reset banner — appears when any *_status has been
                in_progress for >5 minutes. Lets admins manually unstick a package
                without dropping into the DB. The 30-min watchdog will eventually
                auto-reset too, but 5 min is the polite-prompt window. */}
            <StuckGenerationBanner
                pkg={pkg}
                onReset={async () => {
                    try {
                        const res = await adminResetContentGeneration(pkg.package_id);
                        toast.success(
                            res.reset
                                ? `Reset ${res.reset} stuck status flag${res.reset === 1 ? "" : "s"} — Generate / Retry buttons are now re-enabled.`
                                : "Nothing to reset.",
                        );
                        const fresh = await adminGetContentPackage(pkg.package_id);
                        if (fresh) {
                            setPkg(fresh);
                            onUpdate?.(fresh);
                        }
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Reset failed.");
                    }
                }}
            />

            {/* Article-generation in-flight banner (background job) */}
            {pkg.status === "generating" && (
                <div
                    className="mb-5 flex items-center gap-3 rounded-lg border border-bl-primary bg-[var(--bl-teal-tint)] px-4 py-3 text-sm"
                    data-testid="ce-article-generation-banner"
                >
                    <Loader2 className="h-4 w-4 animate-spin text-bl-primary shrink-0" />
                    <div>
                        <div className="font-medium text-bl">
                            Generating article…
                        </div>
                        <div className="text-xs text-bl-muted">
                            Title · sections · FAQ · SEO + schema · internal links · CTAs · mini tool — runs in parallel, takes ~60-90s. This page refreshes automatically.
                        </div>
                    </div>
                </div>
            )}

            {/* Article generation failure surface — actionable error */}
            {pkg.status === "generation_failed" && (
                <div
                    className="mb-5 flex items-start gap-3 rounded-lg border border-[var(--bl-rust)] bg-[var(--bl-rust-soft)] px-4 py-3 text-sm"
                    data-testid="ce-article-generation-failed-banner"
                >
                    <div>
                        <div className="font-medium text-[var(--bl-rust)]">
                            Article generation failed
                        </div>
                        <div className="text-xs text-bl-muted mt-1">
                            {pkg.generation_error || "Unknown error — open the network panel for detail."}
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-asset generation progress / summary banner */}
            {pkg.asset_generation_status === "in_progress" && (
                <div
                    className="mb-5 flex items-center gap-3 rounded-lg border border-bl-primary bg-[var(--bl-teal-tint)] px-4 py-3 text-sm"
                    data-testid="ce-asset-progress-banner"
                >
                    <Loader2 className="h-4 w-4 animate-spin text-bl-primary shrink-0" />
                    <div>
                        <div className="font-medium text-bl">
                            Auto-generating on-brand claymation assets…
                        </div>
                        <div className="text-xs text-bl-muted">
                            Hero image · email newsletter copy · article infographic · 8 Pinterest pin images. ~2-4 min total. This page updates live.
                        </div>
                    </div>
                </div>
            )}
            {pkg.asset_generation_status === "complete_with_errors" &&
                pkg.asset_generation_errors &&
                Object.keys(pkg.asset_generation_errors).length > 0 && (
                <div
                    className="mb-5 rounded-lg border border-[var(--bl-amber)] bg-[var(--bl-amber-tint)] px-4 py-3 text-sm"
                    data-testid="ce-asset-errors-banner"
                >
                    <div className="font-medium text-bl mb-1">
                        Some auto-assets failed ({Object.keys(pkg.asset_generation_errors).length}) — others succeeded. Hit the per-asset Regenerate button to retry.
                    </div>
                    <ul className="list-disc pl-5 text-xs text-bl-muted">
                        {Object.entries(pkg.asset_generation_errors).slice(0, 5).map(([k, v]) => (
                            <li key={k}><strong>{k}:</strong> {v}</li>
                        ))}
                    </ul>
                </div>
            )}
            {pkg.asset_generation_status === "complete" && pkg.asset_generation_summary && (
                <div
                    className="mb-5 text-xs text-bl-muted bl-eyebrow !mt-0"
                    data-testid="ce-asset-success-banner"
                >
                    <CheckCircle2 className="w-3 h-3" />
                    All claymation assets generated · {pkg.asset_generation_summary.social_calls} social posts · {pkg.asset_generation_summary.image_calls} images
                </div>
            )}

            {/* Tab body */}
            <div className="bl-card p-5">
                {tab === "article" && (
                    <>
                        <SectionHeader
                            icon={FileText}
                            title="Article body"
                            onRegen={() => regen("article")}
                            regenLabel="Regenerate article"
                            busy={regenBusy === "article"}
                        />
                        <div className="space-y-4">
                            <label className="text-sm text-bl block">
                                <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                                    Title (H1)
                                </span>
                                <input
                                    type="text"
                                    defaultValue={article.title}
                                    onBlur={(e) =>
                                        updateTitleAndNotes({
                                            article_json: { ...article, title: e.target.value },
                                            title: e.target.value,
                                        })
                                    }
                                    className="w-full bl-input"
                                    data-testid="ce-article-title"
                                />
                            </label>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-bl-muted mb-1">
                                    Hero intro
                                </div>
                                <p className="text-sm text-bl leading-relaxed bg-bl-bg p-3 rounded">
                                    {article.hero_intro}
                                </p>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-bl-muted mb-1">
                                    Key takeaways
                                </div>
                                <ul className="list-disc pl-5 space-y-1">
                                    {(article.key_takeaways || []).map((k, i) => (
                                        <li key={i} className="text-sm text-bl">
                                            {k}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-bl-muted mb-1">
                                    Article sections ({(article.article_sections || []).length})
                                </div>
                                <div className="space-y-3">
                                    {(article.article_sections || []).map((s, i) => (
                                        <details
                                            key={i}
                                            className="border border-bl/40 rounded p-3"
                                        >
                                            <summary className="font-medium text-bl cursor-pointer">
                                                {i + 1}. {s.heading}
                                            </summary>
                                            <p className="mt-2 text-sm text-bl whitespace-pre-wrap">
                                                {s.body_markdown}
                                            </p>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {tab === "images" && (
                    <ImagesTab packageId={pkg.package_id} pkg={pkg} />
                )}

                {tab === "mini_tool" && (
                    <>
                        <SectionHeader
                            icon={Settings}
                            title="Mini tool config"
                            onRegen={() => regen("mini_tool")}
                            regenLabel="Regenerate"
                            busy={regenBusy === "mini_tool"}
                        />
                        {!miniTool ? (
                            <p className="text-sm text-bl-muted">No mini-tool generated.</p>
                        ) : (
                            <div className="space-y-3 text-sm text-bl">
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Title
                                    </div>
                                    {miniTool.title}
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Inputs ({(miniTool.inputs || []).length})
                                    </div>
                                    <ul className="list-disc pl-5">
                                        {(miniTool.inputs || []).map((i, idx) => (
                                            <li key={idx}>
                                                <strong>{i.label}</strong> · {i.type}
                                                {i.options ? ` (${i.options.join(", ")})` : ""}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Rules ({(miniTool.rules || []).length})
                                    </div>
                                    <ul className="list-disc pl-5">
                                        {(miniTool.rules || []).map((r, idx) => (
                                            <li key={idx}>
                                                +{r.score} pts · {r.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Verdicts
                                    </div>
                                    {["low", "moderate", "high"].map((k) =>
                                        miniTool.verdict_copy?.[k] ? (
                                            <div key={k} className="mb-1">
                                                <strong>{k}:</strong>{" "}
                                                {miniTool.verdict_copy[k].headline} —{" "}
                                                {miniTool.verdict_copy[k].guidance}
                                            </div>
                                        ) : null,
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {tab === "faq" && (
                    <>
                        <SectionHeader
                            icon={HelpCircle}
                            title="FAQ"
                            onRegen={() => regen("faq")}
                            regenLabel="Regenerate"
                            busy={regenBusy === "faq"}
                        />
                        <div className="space-y-3">
                            {faq.map((f, i) => (
                                <details
                                    key={i}
                                    className="border border-bl/40 rounded p-3"
                                >
                                    <summary className="font-medium text-bl cursor-pointer">
                                        {f.q}
                                    </summary>
                                    <p className="mt-2 text-sm text-bl">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </>
                )}

                {tab === "seo" && (
                    <>
                        <SectionHeader
                            icon={ListChecks}
                            title="SEO + Schema"
                            onRegen={() => regen("seo")}
                            regenLabel="Regenerate SEO"
                            busy={regenBusy === "seo"}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-3">
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        SEO title
                                    </div>
                                    <code className="text-xs">{seo.seo_title}</code>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Meta description ({(seo.meta_description || "").length}/155)
                                    </div>
                                    <p>{seo.meta_description}</p>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Slug
                                    </div>
                                    <code className="text-xs">/learn/{seo.slug}</code>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                                        Tags
                                    </div>
                                    {(seo.tags || []).map((t) => (
                                        <span
                                            key={t}
                                            className="inline-block mr-1 mb-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-bl-bg text-bl"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-bl-muted mb-1">
                                    JSON-LD schemas
                                </div>
                                <pre className="text-[10px] bg-bl-bg p-2 rounded overflow-x-auto max-h-96 overflow-y-auto">
                                    {JSON.stringify(pkg.schema_json, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </>
                )}

                {tab === "cta_blocks" && (
                    <>
                        <SectionHeader
                            icon={Megaphone}
                            title="CTA blocks"
                            onRegen={() => regen("cta_blocks")}
                            regenLabel="Regenerate"
                            busy={regenBusy === "cta_blocks"}
                        />
                        <div className="space-y-3">
                            {cta.map((c, i) => (
                                <div
                                    key={i}
                                    className="border border-bl/40 rounded p-3 text-sm"
                                >
                                    <div className="text-[10px] uppercase tracking-wide text-bl-muted mb-1">
                                        Placement: {c.placement}
                                    </div>
                                    <div className="font-display font-semibold text-bl">
                                        {c.headline}
                                    </div>
                                    <p className="text-bl-muted mt-1">{c.body}</p>
                                    <div className="mt-2 flex gap-2 text-xs">
                                        <span className="bl-btn-primary text-xs">
                                            {c.primary_button?.label}
                                        </span>
                                        <span className="bl-btn-ghost text-xs">
                                            {c.secondary_button?.label}
                                        </span>
                                    </div>
                                    {c.trust_row && (
                                        <div className="mt-2 text-xs text-bl-muted">
                                            · {c.trust_row.join(" · ")}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {tab === "internal_links" && (
                    <>
                        <SectionHeader
                            icon={LinkIcon}
                            title="Internal links"
                            onRegen={() => regen("internal_links")}
                            regenLabel="Regenerate"
                            busy={regenBusy === "internal_links"}
                        />
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-bl-muted border-b border-bl">
                                    <th className="py-2">Anchor</th>
                                    <th className="py-2">URL</th>
                                    <th className="py-2">Placement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {internalLinks.map((l, i) => (
                                    <tr key={i} className="border-b border-bl/30">
                                        <td className="py-2">{l.anchor}</td>
                                        <td className="py-2">
                                            <code className="text-xs">{l.url}</code>
                                        </td>
                                        <td className="py-2 text-bl-muted">
                                            {l.placement_hint}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {tab === "preview" && (
                    <ArticlePreview pkg={pkg} />
                )}

                {tab === "distribution" && <DistributionTab />}

                {/* YouTube + Short-Form Video tabs were removed in v4.44.2.
                    The V1 Lean Engine ships text + Pinterest only. */}

                {tab === "qa" && (
                    <>
                        <SectionHeader
                            icon={ShieldCheck}
                            title="QA + Publish"
                            onRegen={runQA}
                            regenLabel={qaBusy ? "Checking…" : "Run QA"}
                            busy={qaBusy}
                        />
                        {qaResult ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    {qaResult.ok ? (
                                        <CheckCircle className="h-6 w-6 text-[var(--bl-green)]" />
                                    ) : (
                                        <AlertCircle className="h-6 w-6 text-[var(--bl-rust)]" />
                                    )}
                                    <div>
                                        <div className="font-display font-semibold text-xl text-bl">
                                            Score: {qaResult.score}/100
                                        </div>
                                        <div className="text-xs text-bl-muted">
                                            {qaResult.errors} errors · {qaResult.warnings} warnings
                                        </div>
                                    </div>
                                </div>
                                <ul className="space-y-1 text-sm">
                                    {qaResult.findings.map((f, i) => (
                                        <li
                                            key={i}
                                            className={
                                                f.severity === "error"
                                                    ? "text-[var(--bl-rust)]"
                                                    : f.severity === "warning"
                                                      ? "text-[var(--bl-amber)]"
                                                      : "text-bl-muted"
                                            }
                                        >
                                            [{f.severity}] {f.code}: {f.message}
                                        </li>
                                    ))}
                                </ul>
                                {!qaResult.ok && (
                                    <div className="pt-2">
                                        <button
                                            type="button"
                                            onClick={runAutoFix}
                                            disabled={qaFixBusy || qaFixApplying || publishing}
                                            className="bl-btn-primary gap-2"
                                            data-testid="ce-qa-autofix-btn"
                                            title="Preview a before/after diff, then apply: repair missing slug/meta/CTAs/disclaimer, soften banned language, strip placeholders, re-queue failed images."
                                        >
                                            {qaFixBusy ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Wrench className="h-4 w-4" />
                                            )}
                                            {qaFixBusy ? "Computing diff…" : "Preview Auto-Fix"}
                                        </button>
                                        <p className="text-[11px] text-bl-muted mt-1.5">
                                            Opens a before/after diff modal. Nothing is saved until you click <strong>Apply Fix</strong>.
                                        </p>
                                    </div>
                                )}
                                {lastFixReport && (
                                    <div
                                        className="bl-card p-3 mt-3 border-l-4 border-l-[var(--bl-teal)] text-xs"
                                        data-testid="ce-qa-autofix-report"
                                    >
                                        <div className="font-semibold text-bl mb-1.5">
                                            Auto-fix report
                                        </div>
                                        {(lastFixReport.applied_fixes || []).length === 0 ? (
                                            <div className="text-bl-muted">No structural fixes were necessary.</div>
                                        ) : (
                                            <ul className="space-y-0.5 list-disc pl-4 text-bl-muted">
                                                {lastFixReport.applied_fixes.map((line, i) => (
                                                    <li key={i}>{line}</li>
                                                ))}
                                            </ul>
                                        )}
                                        {lastFixReport.images_regen_queued > 0 && (
                                            <div className="mt-2 text-[var(--bl-teal)]">
                                                Re-queued {lastFixReport.images_regen_queued} failed image asset(s) — watch the Images tab.
                                            </div>
                                        )}
                                    </div>
                                )}
                                {qaResult.ok && pkg.status !== "published" && (
                                    <button
                                        type="button"
                                        onClick={publish}
                                        disabled={publishing}
                                        className="bl-btn-primary gap-2 mt-3"
                                        data-testid="ce-qa-publish-btn"
                                    >
                                        {publishing ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Rocket className="h-4 w-4" />
                                        )}
                                        Publish to Learn section
                                    </button>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-bl-muted">
                                Click "Run QA" to check the package against the publish
                                checklist.
                            </p>
                        )}
                    </>
                )}
            </div>
            <QAAutoFixPreviewModal
                open={qaFixModalOpen}
                report={qaFixPreview}
                loading={qaFixBusy && !qaFixPreview}
                applying={qaFixApplying}
                onClose={closeAutoFixModal}
                onApply={applyAutoFix}
            />
        </div>
    );
}

function ArticlePreview({ pkg }) {
    const article = pkg.article_json || {};
    const cta = (pkg.cta_blocks || []).find((c) => c.placement === "end") ||
        (pkg.cta_blocks || [])[0];
    return (
        <div data-testid="ce-article-preview">
            <SectionHeader icon={Eye} title="Mobile + desktop preview" />
            <div className="prose prose-sm max-w-none text-bl">
                <h1 className="font-display font-semibold text-3xl text-bl">
                    {article.title}
                </h1>
                <p className="text-bl-muted">{article.hero_intro}</p>
                {article.key_takeaways && (
                    <div className="my-4 p-4 bg-bl-bg rounded border-l-4 border-bl-primary">
                        <h3 className="font-semibold mb-2">Key takeaways</h3>
                        <ul className="list-disc pl-5">
                            {article.key_takeaways.map((k, i) => (
                                <li key={i}>{k}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {(article.article_sections || []).map((s, i) => (
                    <div key={i}>
                        <h2 className="font-display font-semibold text-xl mt-5">
                            {s.heading}
                        </h2>
                        <div className="whitespace-pre-wrap">{s.body_markdown}</div>
                    </div>
                ))}
                {cta && (
                    <div className="my-6 p-5 bg-bl-bg rounded border border-bl-primary text-center">
                        <h3 className="font-display font-semibold text-xl">
                            {cta.headline}
                        </h3>
                        <p className="text-bl-muted">{cta.body}</p>
                        <div className="mt-3 flex gap-2 justify-center">
                            <span className="bl-btn-primary">{cta.primary_button?.label}</span>
                            <span className="bl-btn-ghost">{cta.secondary_button?.label}</span>
                        </div>
                    </div>
                )}
                <hr className="my-4" />
                <p className="text-xs text-bl-muted italic">{pkg.disclaimer}</p>
            </div>
        </div>
    );
}


// ─────────── Stuck-generation banner ───────────

/** When ANY of the three `*_status` flags has been "in_progress" for more
 * than 5 minutes (started_at + 5min < now), show a calm amber banner with
 * a single Reset button that flips the flag to "failed" and re-enables
 * Generate / Retry. The 30-min watchdog will eventually catch packages
 * even without admin action — this is just the polite-prompt window. */
function StuckGenerationBanner({ pkg, onReset }) {
    const STUCK_THRESHOLD_MS = 5 * 60 * 1000;
    const now = Date.now();
    const stuckKeys = [];
    const probes = [
        ["asset_generation_status", "asset_generation_started_at", "asset generation"],
        ["distribution_generation_status", "distribution_generation_started_at", "platform distribution"],
        ["hero_regeneration_status", "hero_regeneration_started_at", "hero image regeneration"],
        ["short_form_video_status", "short_form_video_started_at", "short-form video generation"],
    ];
    for (const [statusKey, startedKey, label] of probes) {
        if (pkg?.[statusKey] !== "in_progress") continue;
        const startedAt = pkg?.[startedKey];
        if (!startedAt) continue;
        const ageMs = now - new Date(startedAt).getTime();
        if (ageMs > STUCK_THRESHOLD_MS) {
            stuckKeys.push({ label, ageMin: Math.round(ageMs / 60000) });
        }
    }
    const [busy, setBusy] = useState(false);
    if (stuckKeys.length === 0) return null;
    const oldest = stuckKeys.reduce((a, b) => (a.ageMin > b.ageMin ? a : b));
    return (
        <div
            className="mb-5 rounded-lg border border-[var(--bl-amber)] bg-[var(--bl-amber-tint)] px-4 py-3 text-sm flex items-start gap-3"
            data-testid="ce-stuck-banner"
        >
            <AlertTriangle className="h-5 w-5 text-[var(--bl-amber)] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="font-medium text-bl">
                    Looks stuck — {oldest.label} has been running for {oldest.ageMin}+ min
                </div>
                <div className="text-xs text-bl-muted mt-0.5">
                    The background task may have crashed or the backend restarted mid-run. Click Reset
                    to clear the in-progress flag so you can re-fire the Generate / Retry buttons.
                    {stuckKeys.length > 1 && ` (${stuckKeys.length} flags stuck.)`}
                </div>
            </div>
            <button
                type="button"
                onClick={async () => {
                    setBusy(true);
                    try {
                        await onReset?.();
                    } finally {
                        setBusy(false);
                    }
                }}
                disabled={busy}
                data-testid="ce-stuck-reset-btn"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--bl-amber)] bg-white text-xs font-medium text-bl hover:bg-[var(--bl-amber-tint)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
                {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                )}
                Reset status
            </button>
        </div>
    );
}
